import React, { type JSX } from 'react';
import MuiCard from '@mui/material/Card';
import MuiCardHeader from '@mui/material/CardHeader';
import MuiCardContent from '@mui/material/CardContent';
import MuiCardActions from '@mui/material/CardActions';
import { type Theme } from '@mui/material/styles';
import { CommonPropsAndTypes } from '../_Types/StandardComponentProps';
import { themeColors } from '../ThemeProvider/AvenyThemeColors';

interface IProps extends CommonPropsAndTypes.IContentSpacingProps {
  title?: React.ReactNode;
  subtitle?: string;
  avatar?: JSX.Element;
  titleAction?: JSX.Element;
  titleComp?: JSX.Element;
  actions?: any[];
  children?: React.ReactNode;
  width?: CommonPropsAndTypes.ManualSizeType | null;
  height?: CommonPropsAndTypes.ManualSizeType;
  maxWidth?: CommonPropsAndTypes.ManualSizeType;
  minWidth?: CommonPropsAndTypes.ManualSizeType;
  flex?: boolean;
  elevation?: number;
  dataTest?: string;
  small?: boolean;
  testId?: string;
  widgetStyle?: boolean;
  cardStyle?: boolean;
  cardContainerStyle?: boolean;
  smallMargins?: boolean;
  noBottomMargin?: boolean;
  allowHorzScroll?: boolean;
}

/**
Panel

Används för att samla ihop delar av ett formulär som ska vara tydligt separata.
Förändringar som påverkar data i panelen ska göras med kontroller inne i panelen.

Nu implementerad som Card i material design https://material.io/guidelines/components/cards.html# men ev. finns det andra alternativ.

FieldGroup kan användas för att gruppera fält för bättre läsbarhet utan att det ska behandlas som olika objekt.
*/

const noActions: any[] = [];

export const EDPanel: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = (props: IProps) => {
  const inlineStyle = {
    width: props.width === null ? undefined : props.width ?? '100%',
    height: props.height,
    maxWidth: props.maxWidth,
    minWidth: props.minWidth || 320,
    flex: props.flex ? '1 1 auto' : 'none',
  };
  let headerPadding = 16;
  switch (props.headerPadding ?? 'none') {
    case 'none':
      headerPadding = 0;
      break;
    case 'dense':
      headerPadding = 8;
      break;
    default:
      headerPadding = 16;
  }
  let contentPadding = 16;
  switch (props.contentPadding ?? 'normal') {
    case 'none':
      contentPadding = 0;
      break;
    case 'dense':
      contentPadding = 8;
      break;
    default:
      contentPadding = 16;
  }

  return (
    <MuiCard
      style={
        props.height === undefined
          ? inlineStyle
          : {
              ...inlineStyle,
              display: 'flex',
              flexDirection: 'column',
            }
      }
      sx={[
        theme => ({
          [theme.breakpoints.down('sm')]: {
            margin: theme.spacing(1),
            ...(!props.small && {
              maxWidth: `calc(100% - ${theme.spacing(2)}) !important`,
              minWidth: `calc(100% - ${theme.spacing(2)}) !important`,
              overflowX: 'auto !important',
            }),
          },
        }),
        ...(props.cardStyle
          ? [
              (theme: Theme) => ({
                margin: theme.spacing(2),
                overflow: 'auto',
                display: 'flex',
                ...(props.cardContainerStyle && {
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  overflowY: 'auto',
                  "& [class*='MuiGrid-container']": {
                    padding: theme.spacing(1, 0, 2),
                    [theme.breakpoints.only('xs')]: {
                      padding: theme.spacing(1, 0, 3),
                    },
                  },
                  "& [class*='MuiGrid-container'] > div:only-child:not([class*='MuiGrid-item'])":
                    {
                      background: theme.palette.background.paper,
                      boxShadow: theme.shadows[2],
                      borderRadius: theme.spacing(0.5),
                      margin: theme.spacing(0, 0, -3),
                      height: `calc(100% + ${theme.spacing(1)})`,
                      [theme.breakpoints.only('xs')]: {
                        margin: theme.spacing(-1, 0, -3),
                        height: `calc(100% + ${theme.spacing(4)})`,
                      },
                    },
                }),
                ...(props.smallMargins && {
                  margin: theme.spacing(0.5),
                }),
                ...(props.small && {
                  '&': { margin: 0 },
                }),
                ...(props.noBottomMargin && {
                  marginBottom: theme.spacing(-1.25),
                  background: 'none',
                }),
                ...(props.allowHorzScroll && {
                  overflowX: 'auto',
                }),
              }),
            ]
          : []),
      ]}
      elevation={props.elevation ?? 2}
      data-testid={props.testId}
    >
      <MuiCardHeader
        title={props.title ?? ''}
        subheader={props.subtitle ?? ''}
        avatar={props.avatar}
        action={props.titleAction}
        style={{
          padding: headerPadding,
        }}
        sx={{
          backgroundColor: props.widgetStyle
            ? themeColors.blueGray[50]
            : undefined,
          borderBottom: props.widgetStyle
            ? `1px solid ${themeColors.blueGray[100]}`
            : undefined,
        }}
      >
        {props.titleComp}
      </MuiCardHeader>
      {props.children && (
        <MuiCardContent
          style={{
            padding: contentPadding,
            width: '100%',
            boxSizing: 'border-box',
            ...(props.height === undefined
              ? {}
              : { flex: '1 1 auto', display: 'flex', flexDirection: 'column' }),
          }}
          sx={props.cardStyle ? { display: 'flex', height: '100%' } : undefined}
          data-test={props.dataTest}
        >
          {props.children}
        </MuiCardContent>
      )}
      {props.actions && props.actions.length > 0 && (
        <MuiCardActions>{props.actions ?? noActions}</MuiCardActions>
      )}
    </MuiCard>
  );
};

export default EDPanel;
